import {
  API,
  LOCALE_PARAM,
  MATCH_PARAM,
  PAIR_PARAM,
  REMOTE_PARAM,
  SETTINGS_PARAM,
  type NetworkInfo,
} from '@aiscoreboard/contracts';
import { createRemoteControl } from './api.ts';

/**
 * The match lives in the URL as `?m=<id>`.
 *
 * That is what makes a match shareable at all: a QR code is only a picture of
 * a link, so the link has to carry everything the second device needs.
 */
export function matchIdFromUrl(): string | null {
  const value = new URLSearchParams(location.search).get(MATCH_PARAM);
  return value && value.trim() !== '' ? value : null;
}

export function youtubeReturnFromUrl(): boolean {
  if (matchIdFromUrl()) return false;
  const status = new URLSearchParams(location.search).get('youtube');
  return status === 'connected' || status === 'error' || status === 'ready';
}

function removeYouTubeReturn(url: URL): void {
  if (!url.searchParams.has('youtube')) return;
  url.searchParams.delete('youtube');
  url.searchParams.delete('device');
}

export function clearYouTubeReturnFromUrl(): void {
  const url = new URL(location.href);
  removeYouTubeReturn(url);
  history.replaceState(null, '', url);
}

/** A pairing code handed to this device by a scoreboard's QR code. */
export function pairCodeFromUrl(): string | null {
  const value = new URLSearchParams(location.search).get(PAIR_PARAM);
  return value && value.trim() !== '' ? value : null;
}

/** Whether this device was sent here to edit an account, by a settings QR code. */
export function settingsFromUrl(): boolean {
  return new URLSearchParams(location.search).get(SETTINGS_PARAM) === '1';
}

/**
 * A link that opens the settings screen on a phone.
 *
 * Carries nothing but the request: whoever scans it still has to sign in with
 * their own password, so the code is safe to leave on a board in a club room.
 */
export async function settingsUrl(locale?: string): Promise<string> {
  return reachableUrl((url) => url.searchParams.set(SETTINGS_PARAM, '1'), locale);
}

/**
 * The match this device has been asked to be a remote control for.
 *
 * Read straight off the address bar rather than kept in storage: the remote is
 * whatever the code in somebody's hand said it was, and a phone that scanned
 * one match yesterday must not reopen it today.
 */
export function remoteMatchIdFromUrl(): string | null {
  const value = new URLSearchParams(location.search).get(REMOTE_PARAM);
  return value && value.trim() !== '' ? value : null;
}

/** Drop the pairing code once it has been used, so a refresh does not retry it. */
export function clearPairFromUrl(): void {
  const url = new URL(location.href);
  url.searchParams.delete(PAIR_PARAM);
  history.replaceState(null, '', url);
}

/** Keep the phone on its profile after reload without retaining a used pairing code. */
export function putSettingsInUrl(): void {
  const url = new URL(location.href);
  url.searchParams.delete(PAIR_PARAM);
  url.searchParams.delete(MATCH_PARAM);
  url.searchParams.delete(REMOTE_PARAM);
  removeYouTubeReturn(url);
  url.searchParams.set(SETTINGS_PARAM, '1');
  url.hash = '';
  history.replaceState(null, '', url);
}

export function putMatchInUrl(matchId: string | null): void {
  const url = new URL(location.href);
  if (matchId) {
    url.searchParams.set(MATCH_PARAM, matchId);
    removeYouTubeReturn(url);
  }
  else url.searchParams.delete(MATCH_PARAM);
  // Replace rather than push: joining a match is not a navigation step, and
  // a stack of them would make the back button behave strangely.
  history.replaceState(null, '', url);
}

const LOOPBACK = new Set(['localhost', '127.0.0.1', '::1', '[::1]', '0.0.0.0']);

/**
 * A link that turns another device into a remote control for this match.
 *
 * The QR code on the scoreboard points here rather than at the full app. A
 * phone that scans a code at the table is being picked up to score a shot, and
 * handing it a second copy of a scoreboard designed to be read across a room
 * answers a question nobody asked.
 *
 * When the scoreboard is browsed at `localhost` its own URL is useless to a
 * phone — `localhost` would resolve to the phone — so the host is swapped for
 * a LAN address the server reports. The port is kept, because in development
 * the app is served by Vite on 5173 and only Vite proxies the API.
 */
export async function remoteUrl(matchId: string, locale?: string): Promise<string> {
  const grant = await createRemoteControl(matchId);
  return reachableUrl((url) => {
    url.searchParams.set(REMOTE_PARAM, matchId);
    url.hash = new URLSearchParams({ control: grant.token }).toString();
  }, locale);
}

/**
 * A link that opens the pairing on a phone, reachable from that phone.
 *
 * Shares `reachableUrl` with the handoff link on purpose: the `localhost`
 * problem and its fix are identical, and having two copies of that reasoning
 * is how one of them ends up wrong.
 */
export async function pairUrl(code: string, locale?: string): Promise<string> {
  return reachableUrl((url) => url.searchParams.set(PAIR_PARAM, code), locale);
}

/** Strip this device's URL down and swap in an address a phone can reach. */
async function reachableUrl(apply: (url: URL) => void, locale?: string): Promise<string> {
  const url = new URL(location.href);
  url.search = '';
  url.hash = '';
  apply(url);
  // The phone is standing in the same room as the board, so it should speak
  // the language the board was set to rather than the one its owner's browser
  // happens to prefer.
  if (locale) url.searchParams.set(LOCALE_PARAM, locale);

  if (!LOOPBACK.has(url.hostname)) return url.toString();

  try {
    const response = await fetch(API.network);
    if (response.ok) {
      const info = (await response.json()) as NetworkInfo;
      const address = info.addresses[0];
      if (address) url.hostname = address;
    }
  } catch {
    // Offline or the server is down: fall back to the loopback URL, which at
    // least still works on this device.
  }

  return url.toString();
}
